import { useURLFilters } from '~/screens/MyAgency/hooks/useURLFilters';
import { useMyAgencyStore } from '~/screens/MyAgency/store/useMyAgencyStore';
import { FilterMap, Filters, Platform } from '~/screens/MyAgency/types';

export type AdvancedFilters = {
  /** This returns an array of applied filters */
  appliedFilters: Filters[];
  /** This sets a new filter value */
  setFilter: <P extends Platform, K extends keyof FilterMap[P]>(
    platform: P,
    filterType: K,
    value: FilterMap[P][K],
  ) => void;
  /** This gets the value of an applied filter */
  getFilter: <P extends Platform, K extends keyof FilterMap[P]>(
    platform: P,
    filterType: K,
  ) => FilterMap[P][K] | undefined;
  /** This clears one or more filters */
  clearFilters: (
    platform?: Platform,
    filterTypes?: (keyof FilterMap[Platform])[],
  ) => void;
  /** The active platform */
  activePlatform: Platform;
  /** Set the active platform */
  setActivePlatform: (platform: Platform) => void;
  /** Drawer state */
  isDrawerOpen: boolean;
  /** Set the drawer state */
  setIsDrawerOpen: (isOpen: boolean) => void;
};

// Custom hook for managing advanced filters
export const useAdvancedFiltersStore = (): AdvancedFilters => {
  const {
    advancedFiltersActivePlatform: activePlatform,
    setAdvancedFiltersActivePlatform: setActivePlatform,
    isAdvancedFiltersDrawerOpen: isDrawerOpen,
    setIsAdvancedFiltersDrawerOpen: setIsDrawerOpen,
  } = useMyAgencyStore();

  const {
    appliedFilters,
    getFilter,
    setFilter,
    clearFilters: clearUrlFilters,
  } = useURLFilters();

  const clearFiltersAndResetPlatform = (
    platform?: Platform,
    filterTypes?: (keyof FilterMap[Platform])[],
  ) => {
    clearUrlFilters(platform, filterTypes);
    setActivePlatform('Select Platform' as Platform);
  };

  return {
    getFilter,
    setFilter,
    clearFilters: clearFiltersAndResetPlatform,
    appliedFilters,
    activePlatform,
    setActivePlatform,
    isDrawerOpen,
    setIsDrawerOpen,
  };
};
